// src/features/dashboard/hooks/Employee/useEmployeeProductivity.ts

import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { ENV } from '../../../../config/env';

import { formatApiDate } from '../../utils/date.utils';

import {
  useDashboardDate,
} from '../../context/DashboardDateContext';

import {
  EmployeeProductivityRequest,
  EmployeeProductivityResponse,
  EmployeeProductivityKpiResponse,
  HourlyProductivityResponse,
  TimeClassificationResponse,
  ProductivityTrendResponse,
  MeVsTeamResponse,
  MonthlyProductivityTrendResponse,
  ApplicationDetailResponse,
  ApplicationDetailSummary,
} from '../../types/Employee/employeeProductivity.types';

import {
  employeeProductivityMock,
} from '../../mocks/Employee/employeeProductivity.mock';

import {
  getEmployeeProductivityKPIs,
  getHourlyProductivityChart,
  getTimeClassification,
  getProductivityWTDTrend,
  getMeVsTeamProductivity,
  getMonthlyProductivityTrend,
  getApplicationDetails,
} from '../../services/Employee/employeeProductivity.service';

/* ============================================================================
 * HOOK
 * ========================================================================== */

export const useEmployeeProductivity = () => {
  /* ==========================================================================
   * DATE
   * ======================================================================== */

  const {
    fromDate,
    toDate,
  } = useDashboardDate();

  /* ==========================================================================
   * HEADER
   * ======================================================================== */

  const [
    header,
    setHeader,
  ] = useState({
    todayProductivity: 0,
    aboveTarget: 0,
    target: 80,
    businessUnit: '',
  });

  /* ==========================================================================
   * HOURLY HEADER
   * ======================================================================== */

  const [
    hourlyHeader,
    setHourlyHeader,
  ] = useState({
    title: '',
  });

  /* ==========================================================================
   * TARGETS
   * ======================================================================== */

  const [
    monthlyTarget,
    setMonthlyTarget,
  ] = useState(80);

  const [
    productivityTarget,
    setProductivityTarget,
  ] = useState(80);

  /* ==========================================================================
   * LOADING
   * ======================================================================== */

  const [
    loading,
    setLoading,
  ] = useState(true);

  /* ==========================================================================
   * SUMMARY CARDS
   * ======================================================================== */

  const [
    summaryCards,
    setSummaryCards,
  ] = useState<
    EmployeeProductivityResponse['summaryCards']
  >([]);

  /* ==========================================================================
   * HOURLY PRODUCTIVITY
   * ======================================================================== */

  const [
    hourlyProductivity,
    setHourlyProductivity,
  ] = useState<
    EmployeeProductivityResponse['hourlyProductivity']
  >([]);

  /* ==========================================================================
   * TIME CLASSIFICATION
   * ======================================================================== */

  const [
    timeClassification,
    setTimeClassification,
  ] = useState<
    EmployeeProductivityResponse['timeClassification']
  >([]);

  /* ==========================================================================
   * PRODUCTIVITY TREND
   * ======================================================================== */

  const [
    productivityTrend,
    setProductivityTrend,
  ] = useState<
    EmployeeProductivityResponse['productivityTrend']
  >([]);

  /* ==========================================================================
   * ME VS TEAM
   * ======================================================================== */

  const [
    meVsTeam,
    setMeVsTeam,
  ] = useState<
    EmployeeProductivityResponse['meVsTeam']
  >([]);

  /* ==========================================================================
   * APPLICATION DETAILS
   * ======================================================================== */

  const [
    applicationDetails,
    setApplicationDetails,
  ] = useState<
    EmployeeProductivityResponse['applicationDetails']
  >([]);

  /* ==========================================================================
   * APPLICATION SUMMARY
   *
   * IMPORTANT:
   *
   * This stores:
   *
   * {
   *   totalDuration: "08:16:48",
   *   productivePercentage: 72.5
   * }
   * ======================================================================== */

  const [
    applicationDetailSummary,
    setApplicationDetailSummary,
  ] = useState<ApplicationDetailSummary | null>(
    null,
  );

  /* ==========================================================================
   * MONTHLY PRODUCTIVITY
   * ======================================================================== */

  const [
    monthlyProductivityTrend,
    setMonthlyProductivityTrend,
  ] = useState<
    EmployeeProductivityResponse['monthlyProductivityTrend']
  >([]);

  /* ==========================================================================
   * REQUEST VERSION
   * ======================================================================== */

  const requestVersionRef =
    useRef(0);

  /* ==========================================================================
   * RESET DATA
   * ======================================================================== */

  const resetData = useCallback(() => {
    setHeader({
      todayProductivity: 0,
      aboveTarget: 0,
      target: 80,
      businessUnit: '',
    });

    setHourlyHeader({
      title: '',
    });

    setMonthlyTarget(80);

    setProductivityTarget(80);

    setSummaryCards([]);

    setHourlyProductivity([]);

    setTimeClassification([]);

    setProductivityTrend([]);

    setMeVsTeam([]);

    setApplicationDetails([]);

    /* IMPORTANT */
    setApplicationDetailSummary(null);

    setMonthlyProductivityTrend([]);
  }, []);

  /* ==========================================================================
   * LOAD DATA
   * ======================================================================== */

  const loadData = useCallback(
    async () => {
      const currentRequestVersion =
        ++requestVersionRef.current;

      const isLatestRequest = () =>
        currentRequestVersion ===
        requestVersionRef.current;

      /* ======================================================================
       * START LOADING
       * ==================================================================== */

      setLoading(true);

      /* ======================================================================
       * CLEAR OLD DATA
       * ==================================================================== */

      resetData();

      try {
        /* ====================================================================
         * MOCK DATA
         * ================================================================== */

        if (ENV.USE_MOCK) {
          if (!isLatestRequest()) {
            return;
          }

          setSummaryCards(
            employeeProductivityMock.summaryCards,
          );

          setHourlyProductivity(
            employeeProductivityMock.hourlyProductivity,
          );

          setTimeClassification(
            employeeProductivityMock.timeClassification,
          );

          setProductivityTrend(
            employeeProductivityMock.productivityTrend,
          );

          setMeVsTeam(
            employeeProductivityMock.meVsTeam,
          );

          setApplicationDetails(
            employeeProductivityMock.applicationDetails,
          );

          setMonthlyProductivityTrend(
            employeeProductivityMock.monthlyProductivityTrend,
          );

          setLoading(false);

          return;
        }

        /* ====================================================================
         * EMPLOYEE CODE
         * ================================================================== */

        const employeeCode =
          localStorage.getItem(
            'employeeCode',
          ) || '';

        if (!employeeCode) {
          console.error(
            'Employee code is missing.',
          );

          if (isLatestRequest()) {
            setLoading(false);
          }

          return;
        }

        /* ====================================================================
         * API DATE
         * ================================================================== */

        const formattedFromDate =
          formatApiDate(
            fromDate,
          );

        const formattedToDate =
          formatApiDate(
            toDate,
          );

        const request: EmployeeProductivityRequest = {
          employeeCode,
          date:
            formattedFromDate ===
            formattedToDate
              ? formattedFromDate
              : formattedFromDate,
        };

        /* ====================================================================
         * API VARIABLES
         * ================================================================== */

        let kpi:
          | EmployeeProductivityKpiResponse
          | null = null;

        let hourly:
          | HourlyProductivityResponse
          | null = null;

        let classification:
          TimeClassificationResponse[] = [];

        let wtd:
          | ProductivityTrendResponse
          | null = null;

        let team:
          | MeVsTeamResponse
          | null = null;

        let monthly:
          | MonthlyProductivityTrendResponse
          | null = null;

        let applications:
          | ApplicationDetailResponse
          | null = null;

        /* ====================================================================
         * KPI
         * ================================================================== */

        try {
          kpi =
            await getEmployeeProductivityKPIs(
              request,
            );
        } catch (error) {
          console.error(
            'KPI Error:',
            error,
          );
        }

        if (!isLatestRequest()) {
          return;
        }

        /* ====================================================================
         * HOURLY
         * ================================================================== */

        try {
          hourly =
            await getHourlyProductivityChart(
              request,
            );
        } catch (error) {
          console.error(
            'Hourly Productivity Error:',
            error,
          );
        }

        if (!isLatestRequest()) {
          return;
        }

        /* ====================================================================
         * TIME CLASSIFICATION
         * ================================================================== */

        try {
          classification =
            await getTimeClassification(
              request,
            );
        } catch (error) {
          console.error(
            'Time Classification Error:',
            error,
          );
        }

        if (!isLatestRequest()) {
          return;
        }

        /* ====================================================================
         * WTD TREND
         * ================================================================== */

        try {
          wtd =
            await getProductivityWTDTrend(
              request,
            );
        } catch (error) {
          console.error(
            'WTD Trend Error:',
            error,
          );
        }

        if (!isLatestRequest()) {
          return;
        }

        /* ====================================================================
         * ME VS TEAM
         * ================================================================== */

        try {
          team =
            await getMeVsTeamProductivity(
              request,
            );
        } catch (error) {
          console.error(
            'Me vs Team Error:',
            error,
          );
        }

        if (!isLatestRequest()) {
          return;
        }

        /* ====================================================================
         * MONTHLY
         * ================================================================== */

        try {
          monthly =
            await getMonthlyProductivityTrend(
              request,
            );
        } catch (error) {
          console.error(
            'Monthly Trend Error:',
            error,
          );
        }

        if (!isLatestRequest()) {
          return;
        }

        /* ====================================================================
         * APPLICATION DETAILS
         *
         * IMPORTANT:
         *
         * applications is an OBJECT:
         *
         * {
         *   details: [],
         *   summary: {}
         * }
         * ================================================================== */

        try {
          applications =
            await getApplicationDetails(
              request,
            );
        } catch (error) {
          console.error(
            'Application Detail Error:',
            error,
          );

          applications = null;
        }

        if (!isLatestRequest()) {
          return;
        }

        /* ====================================================================
         * KPI DATA
         * ================================================================== */

        if (kpi) {
          const todayProductivity =
            Number(
              kpi.todayProductivity,
            ) || 0;

          const productivityVariance =
            Number(
              kpi.productivityVariance,
            ) || 0;

          setHeader({
            todayProductivity,
            aboveTarget:
              productivityVariance,
            target: 80,
            businessUnit:
              kpi.businessUnit || '',
          });

          setSummaryCards([
            {
              id: 1,
              title:
                "Today's Productivity",
              value:
                `${todayProductivity}%`,
              subtitle:
                `${
                  productivityVariance > 0
                    ? '+'
                    : ''
                }${productivityVariance}% vs 80% target`,
              icon: 'Zap',
              iconBg: '#E8F8F0',
              iconColor: '#18A957',
            },

            {
              id: 2,
              title: 'Monthly Avg',
              value:
                `${Number(
                  kpi.monthlyAverage,
                ) || 0}%`,
              subtitle:
                'Month to date',
              icon: 'BarChart3',
              iconBg: '#EAF3FF',
              iconColor: '#3B82F6',
            },

            {
              id: 3,
              title: 'Productive Time',
              value:
                kpi.productiveTime ||
                '0m',
              subtitle:
                `Of ${
                  kpi.activeTime ||
                  '0m'
                } active`,
              icon: 'Clock3',
              iconBg: '#F3E8FF',
              iconColor: '#7C3AED',
            },

            {
              id: 4,
              title: 'Non-Prod Time',
              value:
                kpi.nonProductiveTime ||
                '0m',
              subtitle:
                kpi.nonProductiveSubCategories?.trim() ||
                'No non-productive sub-categories',
              icon: 'CircleOff',
              iconBg: '#FFF3E8',
              iconColor: '#F97316',
            },

            {
              id: 5,
              title: 'Team Rank',
              value:
                `${kpi.teamRank} / ${kpi.teamSize}`,
              subtitle:
                `Manager: ${
                  kpi.managerName ||
                  'N/A'
                }`,
              icon: 'Users',
              iconBg: '#FCE7F3',
              iconColor: '#DB2777',
            },
          ]);
        }

        /* ====================================================================
         * HOURLY PRODUCTIVITY
         * ================================================================== */

        if (
          hourly &&
          Array.isArray(
            hourly.trend,
          )
        ) {
          const mappedHourly =
            hourly.trend.map(
              (item) => ({
                time:
                  `${String(
                    item.hour,
                  ).padStart(
                    2,
                    '0',
                  )}:00`,

                productive:
                  Number(
                    item.productivePercentage,
                  ) || 0,

                nonProductive:
                  Number(
                    item.nonProductivePercentage,
                  ) || 0,

                neutral:
                  Number(
                    item.noImpactPercentage,
                  ) || 0,

                target:
                  Number(
                    hourly.targetProductivityThreshold,
                  ) || 80,
              }),
            );

          setHourlyProductivity(
            mappedHourly,
          );

          const startHour =
            hourly.trend[0]?.hour;

          const endHour =
            hourly.trend[
              hourly.trend.length - 1
            ]?.hour;

          if (
            startHour !== undefined &&
            endHour !== undefined
          ) {
            setHourlyHeader({
              title:
                `${String(
                  startHour,
                ).padStart(
                  2,
                  '0',
                )}:00 - ${String(
                  endHour,
                ).padStart(
                  2,
                  '0',
                )}:00 productivity`,
            });
          }

          setProductivityTarget(
            Number(
              hourly.targetProductivityThreshold,
            ) || 80,
          );
        }

        /* ====================================================================
         * TIME CLASSIFICATION
         * ================================================================== */

        if (
          Array.isArray(
            classification,
          )
        ) {
          const colors: Record<
            string,
            string
          > = {
            Productive:
              '#22C55E',

            'Non-Productive':
              '#EF4444',

            'No-Impact':
              '#94A3B8',

            Neutral:
              '#94A3B8',
          };

          setTimeClassification(
            classification.map(
              (item) => ({
                name:
                  item.category,

                value:
                  Number(
                    Number(
                      item.percentage,
                    ).toFixed(
                      1,
                    ),
                  ),

                duration:
                  item.duration,

                color:
                  colors[
                    item.category
                  ] ??
                  '#94A3B8',
              }),
            ),
          );
        }

        /* ====================================================================
         * WTD PRODUCTIVITY TREND
         * ================================================================== */

        if (
          wtd &&
          Array.isArray(
            wtd.trend,
          )
        ) {
          setProductivityTrend(
            wtd.trend.map(
              (item) => {
                const datePart =
                  item.workDate
                    ?.split('T')[0]
                    ?.split('-')[2] ??
                  '';

                const dayName =
                  item.dayLabel
                    ?.split(' ')[0] ??
                  '';

                const shortDay =
                  dayName.substring(
                    0,
                    3,
                  );

                return {
                  day:
                    datePart
                      ? `${shortDay} ${datePart}`
                      : shortDay,

                  productivity:
                    Number(
                      item.productivityPercentage,
                    ) || 0,

                  target:
                    Number(
                      wtd.wtdTargetProductivityThreshold,
                    ) || 80,
                };
              },
            ),
          );

          setProductivityTarget(
            Number(
              wtd.wtdTargetProductivityThreshold,
            ) || 80,
          );
        }

        /* ====================================================================
         * ME VS TEAM
         * ================================================================== */

        if (
          team &&
          Array.isArray(
            team.trend,
          )
        ) {
          const target =
            Number(
              team.targetProductivityThreshold ??
                wtd?.wtdTargetProductivityThreshold ??
                80,
            ) || 80;

          setProductivityTarget(
            target,
          );

          setMeVsTeam(
            team.trend.map(
              (item) => {
                const datePart =
                  item.workDate
                    ?.split('T')[0]
                    ?.split('-')[2] ??
                  '';

                const shortDay =
                  (
                    item.dayName ??
                    ''
                  )
                    .substring(
                      0,
                      3,
                    )
                    .trim();

                const dayLabel =
                  datePart
                    ? `${shortDay} ${datePart}`
                    : shortDay;

                return {
                  day:
                    dayLabel,

                  me:
                    Number(
                      item.myProductivityPercentage,
                    ) || 0,

                  team:
                    Number(
                      item.teamAvgProductivityPercentage,
                    ) || 0,

                  target,
                };
              },
            ),
          );
        }

        /* ====================================================================
         * MONTHLY PRODUCTIVITY
         * ================================================================== */

        if (
          monthly &&
          Array.isArray(
            monthly.trend,
          )
        ) {
          const target =
            Number(
              monthly.targetProductivityThreshold,
            ) || 80;

          setMonthlyTarget(
            target,
          );

          setProductivityTarget(
            target,
          );

          setMonthlyProductivityTrend(
            monthly.trend.map(
              (item) => ({
                day:
                  String(
                    item.dayNumber,
                  ),

                productivity:
                  Number(
                    item.productivityPercentage,
                  ) || 0,
              }),
            ),
          );
        }

        /* ====================================================================
         * APPLICATION DETAILS
         *
         * API:
         *
         * {
         *   details: [...],
         *   summary: {
         *     totalDuration: "08:16:48",
         *     productivePercentage: 72.5
         *   }
         * }
         *
         * IMPORTANT:
         * Store BOTH details and summary.
         * ================================================================== */

        if (
          applications &&
          Array.isArray(
            applications.details,
          )
        ) {
          /* ================================================================
           * STORE API SUMMARY
           * ================================================================ */

          if (
            applications.summary
          ) {
            setApplicationDetailSummary({
              totalDuration:
                applications.summary
                  .totalDuration ||
                '00:00:00',

              productivePercentage:
                Number(
                  applications.summary
                    .productivePercentage,
                ) || 0,
            });
          } else {
            setApplicationDetailSummary(
              null,
            );
          }

          /* ================================================================
           * MAP DETAILS FOR UI
           * ================================================================ */

          setApplicationDetails(
            applications.details.map(
              (
                item,
                index,
              ) => {
                let classification:
                  | 'Productive'
                  | 'Neutral'
                  | 'Non-Prod';

                switch (
                  item.classification?.trim()
                ) {
                  case 'Non-Productive':
                    classification =
                      'Non-Prod';
                    break;

                  case 'No-Impact':
                    classification =
                      'Neutral';
                    break;

                  case 'Productive':
                    classification =
                      'Productive';
                    break;

                  default:
                    classification =
                      'Neutral';
                    break;
                }

                return {
                  id:
                    index + 1,

                  application:
                    item.subCategoryName?.trim() ||
                    'Unknown',

                  time:
                    item.duration ||
                    '00:00:00',

                  percentage:
                    Number(
                      item.percentageOfTotal,
                    ) || 0,

                  classification,

                  category:
                    item.categoryName?.trim() ||
                    'Unknown',
                };
              },
            ),
          );
        } else {
          setApplicationDetails([]);

          setApplicationDetailSummary(
            null,
          );
        }

        /* ====================================================================
         * FINISHED
         * ================================================================== */

        if (isLatestRequest()) {
          setLoading(false);
        }
      } catch (error) {
        console.error(
          'Employee Productivity:',
          error,
        );

        if (isLatestRequest()) {
          setLoading(false);
        }
      }
    },
    [
      fromDate,
      toDate,
      resetData,
    ],
  );

  /* ==========================================================================
   * LOAD WHEN DATE CHANGES
   * ======================================================================== */

  useEffect(() => {
    loadData();

    return () => {
      requestVersionRef.current += 1;
    };
  }, [
    loadData,
  ]);

  /* ==========================================================================
   * RETURN
   * ======================================================================== */

  return {
    loading,

    header,

    hourlyHeader,

    productivityTarget,

    monthlyTarget,

    summaryCards,

    hourlyProductivity,

    timeClassification,

    productivityTrend,

    meVsTeam,

    applicationDetails,

    /* IMPORTANT */
    applicationDetailSummary,

    monthlyProductivityTrend,

    refresh:
      loadData,
  };
};